import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Spinner,
  MasterFormatAutocomplete,
  MasterFormatViewToggle,
} from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { useToast } from '../../../shared/context/ToastContext';
import { formatCurrency, formatUnitCost, formatUnitCostWithUnit, formatNumber } from '../../../shared/utils/formatters';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { useMasterFormatView } from '../hooks/useMasterFormatView';
import { getLegacyDivisionSubtitle } from '../../../shared/utils/masterformatMapping';
import { useTenantPreferences } from '../../../shared/hooks/useTenantPreferences';
import { EstimateDocumentsPanel } from './EstimateDocumentsPanel';
import { MobileLineItemSheet } from './MobileLineItemSheet';

// Phones: tap row → mobile sheet. Desktop: inline editor as before.
const isMobileViewport = () =>
  typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(max-width: 767px)').matches;

// Generate temporary IDs for new items
const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Line item editor states
const LINE_ITEM_STATE = {
  VIEW: 'view',           // Normal view mode
  EDITING: 'editing',     // Editing fields (MasterFormat, qty, unit)
  PREDICTING: 'predicting', // Calling AI API
  REVIEWING: 'reviewing', // Showing AI prediction for approval
  ADJUSTING: 'adjusting', // User is editing the AI prediction
};

// Cost source badge component
function CostSourceBadge({ source, confidenceScore }) {
  const variants = {
    ai: { color: 'blue', label: 'AI Estimate' },
    user_adjusted: { color: 'yellow', label: 'User Adjusted' },
    uploaded: { color: 'yellow', label: 'Uploaded' },
    manual: { color: 'gray', label: 'Manual' },
    template: { color: 'purple', label: 'Template' },
  };

  const { color, label } = variants[source] || variants.manual;

  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant={color} className="text-xs">
        {label}
      </Badge>
      {source === 'ai' && confidenceScore && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          ({Math.round(confidenceScore * 100)}%)
        </span>
      )}
    </span>
  );
}

// AI Explanation Panel component
function ExplanationPanel({ item, onClose, onReclassify }) {
  const [similarItems, setSimilarItems] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  // Per-source-item reclassify state: index -> { code, persistMapping, isSubmitting }
  const [reclassifyState, setReclassifyState] = useState({});

  useEffect(() => {
    if (item.aiSimilarItems && item.aiSimilarItems.length > 0) {
      setLoadingSimilar(true);
      estimatesApi.getLineItemsByIds(item.aiSimilarItems)
        .then(response => setSimilarItems(response.lineItems || []))
        .catch(err => console.error('Failed to load similar items:', err))
        .finally(() => setLoadingSimilar(false));
    }
  }, [item.aiSimilarItems]);

  const hasUserAdjustment = item.userAdjustedCost && item.userAdjustmentReason;
  const hasAIExplanation = !!item.aiExplanation;
  const isUploaded = item.costSource === 'uploaded';
  const hasSourceLineItems = isUploaded && item.sourceLineItems?.length > 0;

  const openReclassify = (idx) => {
    setReclassifyState(s => ({
      ...s,
      [idx]: { code: null, persistMapping: true, isSubmitting: false },
    }));
  };
  const cancelReclassify = (idx) => {
    setReclassifyState(s => {
      const next = { ...s };
      delete next[idx];
      return next;
    });
  };
  const updateReclassifyField = (idx, field, value) => {
    setReclassifyState(s => ({ ...s, [idx]: { ...s[idx], [field]: value } }));
  };
  const applyReclassify = async (idx, source) => {
    const state = reclassifyState[idx];
    if (!state?.code?.code || !onReclassify) return;
    updateReclassifyField(idx, 'isSubmitting', true);
    try {
      await onReclassify({
        sourceDescription: source.description,
        masterformatCode: state.code.code,
        masterformatTitle: state.code.title,
        persistMapping: !!state.persistMapping,
      });
      cancelReclassify(idx);
    } finally {
      updateReclassifyField(idx, 'isSubmitting', false);
    }
  };

  const renderReclassifyForm = (idx, source) => {
    const state = reclassifyState[idx];
    if (!state) return null;
    return (
      <div className="mt-2 p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded space-y-2">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">
          New MasterFormat code:
        </label>
        <MasterFormatAutocomplete
          value={state.code}
          onChange={(val) => updateReclassifyField(idx, 'code', val)}
          autoFocus
        />
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={state.persistMapping}
            onChange={(e) => updateReclassifyField(idx, 'persistMapping', e.target.checked)}
          />
          Remember this mapping for future estimates
        </label>
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            disabled={!state.code?.code || state.isSubmitting}
            onClick={() => applyReclassify(idx, source)}
          >
            {state.isSubmitting ? 'Applying...' : 'Apply'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={state.isSubmitting}
            onClick={() => cancelReclassify(idx)}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderReclassifyButton = (idx) => {
    if (reclassifyState[idx] || !onReclassify) return null;
    return (
      <button
        type="button"
        onClick={() => openReclassify(idx)}
        className="ml-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 underline"
      >
        Reclassify
      </button>
    );
  };

  // Uploaded source items panel
  if (isUploaded && !hasAIExplanation && !hasUserAdjustment) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 mt-2 mb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-2 text-gray-600 dark:text-gray-300 flex-1 min-w-0">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm font-medium flex-1 min-w-0">
              {hasSourceLineItems ? (
                item.sourceLineItems.length === 1 ? (
                  <div>
                    <span>
                      Derived from {item.sourceLineItems[0].description}
                      {item.sourceLineItems[0].totalCost != null && (
                        <> = {formatCurrency(item.sourceLineItems[0].totalCost)}</>
                      )}
                    </span>
                    {renderReclassifyButton(0)}
                    {renderReclassifyForm(0, item.sourceLineItems[0])}
                  </div>
                ) : (
                  <div>
                    <span>Derived from:</span>
                    <ol className="mt-1 ml-4 list-decimal list-inside font-normal space-y-1">
                      {item.sourceLineItems.map((source, idx) => (
                        <li key={idx}>
                          <span>
                            {source.description}
                            {source.totalCost != null && (
                              <> = {formatCurrency(source.totalCost)}</>
                            )}
                          </span>
                          {renderReclassifyButton(idx)}
                          {renderReclassifyForm(idx, source)}
                        </li>
                      ))}
                    </ol>
                  </div>
                )
              ) : (
                <span>This line item was created from an uploaded estimate.</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-2 flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-2 mb-2">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-semibold text-blue-900 dark:text-blue-200">Cost Explanation</h4>
        <button
          onClick={onClose}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* User Adjustment Section (shown first) */}
      {hasUserAdjustment && (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="yellow" className="text-xs">User Adjusted</Badge>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {formatCurrency(item.userAdjustedCost)}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
            {item.userAdjustmentReason}
          </p>
          {item.userAdjustedBy && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              By {item.userAdjustedBy} {item.userAdjustedAt && `on ${new Date(item.userAdjustedAt).toLocaleDateString()}`}
            </p>
          )}
        </div>
      )}

      {/* AI Estimate Section */}
      {hasAIExplanation && (
        <div className={hasUserAdjustment ? 'border-t border-blue-200 dark:border-blue-800 pt-3 mb-3' : 'mb-3'}>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="blue" className="text-xs">AI Estimate</Badge>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {formatCurrency(item.aiEstimatedCost)}
            </span>
            {item.aiConfidenceScore && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                (Confidence: {Math.round(item.aiConfidenceScore * 100)}%)
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
            {item.aiExplanation}
          </p>
        </div>
      )}

      {/* Similar Items Section */}
      {item.aiSimilarItems && item.aiSimilarItems.length > 0 && (
        <div className={`${(hasAIExplanation || hasUserAdjustment) ? 'border-t border-blue-200 dark:border-blue-800 pt-3' : ''}`}>
          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Similar Line Items ({item.aiSimilarItems.length})
          </h5>
          {loadingSimilar ? (
            <div className="flex items-center py-3">
              <Spinner size="sm" />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">Loading similar items...</span>
            </div>
          ) : similarItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-gray-200 dark:border-gray-800 rounded">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="text-left py-2 px-3">Project</th>
                    <th className="text-left py-2 px-3">Description</th>
                    <th className="text-right py-2 px-3">Quantity</th>
                    <th className="text-right py-2 px-3">Unit Cost</th>
                    <th className="text-right py-2 px-3">Total</th>
                    <th className="text-center py-2 px-3">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {similarItems.map((simItem, idx) => (
                    <tr key={simItem.lineItemId || idx} className="border-t border-gray-200 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                      <td className="py-2 px-3">
                        <div className="max-w-[150px]">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={simItem.projectName}>
                            {simItem.projectName}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">{simItem.projectDate}</div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="max-w-[200px] truncate" title={simItem.description}>
                          {simItem.description}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">
                        {formatNumber(simItem.quantity)} {simItem.unit}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatUnitCost(simItem.unitCost)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900 dark:text-gray-100">
                        {formatCurrency(simItem.totalCost)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {simItem.costSource === 'ai' && <Badge variant="blue" className="text-xs">AI</Badge>}
                        {simItem.costSource === 'user_adjusted' && <Badge variant="yellow" className="text-xs">Adj</Badge>}
                        {simItem.costSource === 'manual' && <Badge variant="gray" className="text-xs">Manual</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No similar items found.</p>
          )}
        </div>
      )}
    </div>
  );
}

function LineItemEditor({ item, onUpdate, onDelete, estimateId, divisionNumber, projectContext, categoryName, categoryCode, excludeEstimateId, baseTotalCost = 0, existingLineItemCodes = [], hideAIEstimate = false, isCustomMode = false, pendingFlushesRef = null }) {
  const { showError } = useToast();
  // Determine initial state based on item
  const isNewItem = item.description === 'New Line Item';
  const [state, setState] = useState(
    isNewItem ? LINE_ITEM_STATE.EDITING : LINE_ITEM_STATE.VIEW
  );
  // Mobile sheet: opens on row tap (phones) or when a brand-new item is
  // seeded on a phone. Desktop ignores this state entirely.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(
    isNewItem && isMobileViewport()
  );
  const [showExplanation, setShowExplanation] = useState(false);
  const [editValues, setEditValues] = useState({
    description: item.description,
    masterformatCode: item.masterformatCode || '',
    quantity: item.quantity || '',
    unit: item.unit || '',
    unitCost: item.unitCost || '',
    totalCost: item.totalCost || '',
  });
  // Track whether user is editing unit cost or total (only one at a time)
  const [editMode, setEditMode] = useState('unitCost'); // 'unitCost' or 'total'
  const [selectedMasterFormat, setSelectedMasterFormat] = useState(
    item.masterformatCode ? { code: item.masterformatCode, title: item.description } : null
  );
  const [aiPrediction, setAIPrediction] = useState(null);
  const [similarItems, setSimilarItems] = useState([]);
  const [adjustmentValues, setAdjustmentValues] = useState({ quantity: '', unit: '', unitCost: '', reason: '' });
  const [adjustmentError, setAdjustmentError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [focusField, setFocusField] = useState(null); // 'quantity', 'unit', 'unitCost', or 'total'
  const quantityInputRef = useRef(null);
  const unitInputRef = useRef(null);
  const unitCostInputRef = useRef(null);
  const totalCostInputRef = useRef(null);
  // The editing row element — used by the click-outside listener to detect
  // when the user has navigated away and we should auto-commit edits.
  const editingRowRef = useRef(null);
  // Stable reference to the latest commitEdits closure so callbacks
  // registered once (in useEffect) always see fresh editValues.
  const commitEditsRef = useRef(null);

  // Auto-focus the appropriate field when entering edit mode via row click
  useEffect(() => {
    if (state === LINE_ITEM_STATE.EDITING && focusField && !isNewItem) {
      const refMap = {
        quantity: quantityInputRef,
        unit: unitInputRef,
        unitCost: unitCostInputRef,
        total: totalCostInputRef,
      };
      const ref = refMap[focusField];
      if (ref?.current) {
        ref.current.focus();
        ref.current.select();
      }
      setFocusField(null);
    }
  }, [state, focusField, isNewItem]);

  // Handle clicking a cell in view mode to enter edit mode with focus on that field
  const handleCellClick = (field) => {
    // Phones get the dedicated form sheet instead of the cramped inline editor.
    if (isMobileViewport()) {
      setMobileSheetOpen(true);
      return;
    }
    setFocusField(field);
    // If clicking on total, switch to total edit mode
    if (field === 'total') {
      setEditMode('total');
      // Initialize total from current values
      const currentTotal = editValues.quantity && editValues.unitCost
        ? parseFloat(editValues.quantity) * parseFloat(editValues.unitCost)
        : item.totalCost || '';
      setEditValues(prev => ({ ...prev, totalCost: currentTotal }));
    } else if (field === 'unitCost') {
      setEditMode('unitCost');
    }
    setState(LINE_ITEM_STATE.EDITING);
  };

  const handleMobileSheetSave = (updatedItem) => {
    onUpdate(updatedItem);
    setMobileSheetOpen(false);
    // If this was a brand-new item, the inline editor was in EDITING because
    // of the seed; flip it back to VIEW so the row renders normally.
    if (isNewItem) setState(LINE_ITEM_STATE.VIEW);
  };

  const handleMobileSheetCancel = () => {
    setMobileSheetOpen(false);
    if (isNewItem) {
      // Brand-new item the user backed out of — discard it.
      onDelete();
    }
  };

  // Handle prediction request (called when user presses Enter on new item or clicks "Estimate with AI" on existing item)
  const handlePredict = async () => {
    // For existing items, use the item's masterformatCode if selectedMasterFormat isn't set
    const masterformatCode = selectedMasterFormat?.code || item.masterformatCode;
    const description = selectedMasterFormat?.title || item.description;

    if (!masterformatCode) {
      setValidationError('Please select a MasterFormat code first.');
      return;
    }
    setValidationError('');

    setState(LINE_ITEM_STATE.PREDICTING);

    try {
      // Call AI estimation API
      // Pass excludeEstimateId to prevent the estimate from referencing itself
      const response = await estimatesApi.estimateLineItems(
        projectContext || {},
        [{
          description: description,
          categoryName: categoryName || 'General',
          masterformatCode: masterformatCode,
          quantity: editValues.quantity ? parseFloat(editValues.quantity) : null,
          unit: editValues.unit || null,
        }],
        excludeEstimateId
      );

      const estimate = response.estimates?.[0] || response[0];
      if (estimate?.estimationMethod === 'fallback') {
        console.warn('AI estimation fell back to weighted average:', estimate.failureReason);
      }
      setAIPrediction(estimate);

      // Use similar items from the AI estimation response (unified endpoint)
      setSimilarItems(estimate?.similarItems || []);

      // Pre-fill adjustment values with AI prediction (quantity, unit, and unit cost)
      setAdjustmentValues({
        quantity: estimate?.quantity?.toString() || editValues.quantity?.toString() || '',
        unit: estimate?.unit || editValues.unit || '',
        unitCost: estimate?.unitCost?.toString() || '',
        reason: '',
      });

      setState(LINE_ITEM_STATE.REVIEWING);
    } catch (error) {
      console.error('AI prediction failed:', error);
      showError('Failed to get AI estimate. Please try again.');
      setState(LINE_ITEM_STATE.EDITING);
    }
  };

  // Handle approve - save line item with AI values (quantity, unit, unit cost)
  const handleApprove = () => {
    if (!aiPrediction) return;

    const quantity = aiPrediction.quantity || (editValues.quantity ? parseFloat(editValues.quantity) : 1);
    const unit = aiPrediction.unit || editValues.unit || 'EA';
    const unitCost = aiPrediction.unitCost || 0;
    const totalCost = quantity * unitCost;

    onUpdate({
      ...item,
      description: selectedMasterFormat?.title || item.description,
      masterformatCode: selectedMasterFormat?.code || item.masterformatCode,
      quantity,
      unit,
      unitCost,
      totalCost,
      aiEstimatedCost: totalCost,
      aiExplanation: aiPrediction.explanation,
      aiConfidenceScore: aiPrediction.confidenceScore,
      aiSimilarItems: aiPrediction.similarItemIds,
      costSource: 'ai',
    });

    // Reset state
    setAIPrediction(null);
    setSimilarItems([]);
    setState(LINE_ITEM_STATE.VIEW);
  };

  // Handle adjustment save - save with user-adjusted values (quantity, unit, unit cost)
  const handleAdjustmentSave = () => {
    if (!adjustmentValues.reason || adjustmentValues.reason.trim().split(/\s+/).length < 3) {
      setAdjustmentError('Please provide at least a brief explanation (minimum 3 words).');
      return;
    }

    if (!adjustmentValues.unitCost || isNaN(parseFloat(adjustmentValues.unitCost))) {
      setAdjustmentError('Please enter a valid unit cost.');
      return;
    }

    if (!adjustmentValues.quantity || isNaN(parseFloat(adjustmentValues.quantity))) {
      setAdjustmentError('Please enter a valid quantity.');
      return;
    }

    const quantity = parseFloat(adjustmentValues.quantity);
    const unit = adjustmentValues.unit || aiPrediction?.unit || 'EA';
    const unitCost = parseFloat(adjustmentValues.unitCost);
    const totalCost = quantity * unitCost;

    // Calculate what the AI originally estimated as total (unitCost × quantity)
    const aiEstimatedTotal = (aiPrediction?.quantity || 1) * (aiPrediction?.unitCost || 0);

    onUpdate({
      ...item,
      description: selectedMasterFormat?.title || item.description,
      masterformatCode: selectedMasterFormat?.code || item.masterformatCode,
      quantity,
      unit,
      unitCost,
      totalCost,
      aiEstimatedCost: aiEstimatedTotal,
      aiExplanation: aiPrediction?.explanation,
      aiConfidenceScore: aiPrediction?.confidenceScore,
      userAdjustedCost: totalCost,
      userAdjustmentReason: adjustmentValues.reason.trim(),
      costSource: 'user_adjusted',
    });

    // Reset state
    setAIPrediction(null);
    setSimilarItems([]);
    setAdjustmentValues({ quantity: '', unit: '', unitCost: '', reason: '' });
    setAdjustmentError('');
    setState(LINE_ITEM_STATE.VIEW);
  };

  // Handle cancel - remove new item or reset to view
  const handleCancel = () => {
    if (isNewItem) {
      // Delete the new item
      onDelete();
    } else {
      // Reset edit values and return to view
      setEditValues({
        description: item.description,
        masterformatCode: item.masterformatCode || '',
        quantity: item.quantity || '',
        unit: item.unit || '',
        unitCost: item.unitCost || '',
        totalCost: item.totalCost || '',
      });
      setSelectedMasterFormat(
        item.masterformatCode ? { code: item.masterformatCode, title: item.description } : null
      );
      setAIPrediction(null);
      setSimilarItems([]);
      setAdjustmentValues({ quantity: '', unit: '', unitCost: '', reason: '' });
      setAdjustmentError('');
      setValidationError('');
      setEditMode('unitCost'); // Reset to default mode
      setState(LINE_ITEM_STATE.VIEW);
    }
  };

  // Handle keyboard shortcuts in edit mode
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveExisting();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Commit the current editValues up to the parent. Returns the merged
  // line-item payload (the same object passed to onUpdate) when a commit
  // happened, otherwise null. Callers that need to know the result
  // synchronously (top Save flush) read the return value rather than waiting
  // for parent state to propagate.
  //   options.skipAIAdjustment — true for auto-flush paths (click-outside,
  //     top Save). Skips the AI-cost-adjustment modal flow so we don't
  //     surprise the user with a modal while they're navigating away.
  const commitEdits = ({ skipAIAdjustment = false } = {}) => {
    // Prefer an explicit selection, otherwise fall back to the freshly-typed
    // editValues, otherwise the item's existing values.
    const masterformatCode = selectedMasterFormat?.code || item.masterformatCode;
    // In custom mode the user types a free-form description; the autocomplete
    // (and thus selectedMasterFormat) isn't shown. Its initial title may be
    // the placeholder "New Line Item" carried from the seed item, which would
    // otherwise win the || chain and clobber the typed value.
    const typedDescription =
      editValues.description && editValues.description !== 'New Line Item'
        ? editValues.description
        : null;
    const description = isCustomMode
      ? (typedDescription || item.description)
      : (selectedMasterFormat?.title || typedDescription || item.description);

    // Custom-mode tenants don't pick a CSI code per item — the editor stamps
    // the tenant's anchor division after save, so we only require a code in
    // the standard (GC) flow.
    if (!isCustomMode && !masterformatCode) {
      if (!skipAIAdjustment) {
        setValidationError('Please select a MasterFormat code for the line item.');
      }
      return null;
    }
    setValidationError('');

    const isFeeCategory = categoryCode === '00';
    let quantity, unitCost, totalCost;

    if (isFeeCategory) {
      totalCost = editValues.totalCost ? parseFloat(editValues.totalCost) : item.totalCost;
      quantity = null;
      unitCost = null;
    } else {
      quantity = editValues.quantity ? parseFloat(editValues.quantity) : null;

      if (editMode === 'total') {
        totalCost = editValues.totalCost ? parseFloat(editValues.totalCost) : item.totalCost;
        unitCost = quantity && totalCost ? totalCost / quantity : null;
      } else {
        unitCost = editValues.unitCost ? parseFloat(editValues.unitCost) : null;
        totalCost = quantity && unitCost ? quantity * unitCost : item.totalCost;
      }
    }

    const costChanged = totalCost !== item.totalCost;
    const hasAIEstimate = item.aiEstimatedCost || item.costSource === 'ai';

    if (!skipAIAdjustment && costChanged && hasAIEstimate) {
      // Explicit user action on an AI item with changed cost — surface the
      // adjustment-reason modal. Auto-flush paths skip this.
      setAIPrediction({
        quantity: item.quantity,
        unit: item.unit,
        unitCost: isFeeCategory ? item.totalCost : item.unitCost,
        explanation: item.aiExplanation,
        confidenceScore: item.aiConfidenceScore,
      });
      setAdjustmentValues({
        quantity: isFeeCategory ? '' : (editValues.quantity?.toString() || item.quantity?.toString() || ''),
        unit: isFeeCategory ? '' : (editValues.unit || item.unit || ''),
        unitCost: isFeeCategory ? editValues.totalCost : (editValues.unitCost || (unitCost?.toString() || '')),
        reason: '',
      });
      setState(LINE_ITEM_STATE.ADJUSTING);
      return null;
    }

    const payload = {
      ...item,
      description,
      masterformatCode,
      quantity,
      unit: isFeeCategory ? null : editValues.unit,
      unitCost,
      totalCost,
      costSource: isNewItem
        ? 'manual'
        : (item.costSource === 'ai' && totalCost !== item.totalCost
            ? 'user_adjusted'
            : item.costSource || 'manual'),
    };
    onUpdate(payload);
    setState(LINE_ITEM_STATE.VIEW);
    return payload;
  };

  // Wrapper kept for the AI 'Estimate' button path that needs the modal flow.
  const handleSaveExisting = () => commitEdits({ skipAIAdjustment: false });

  // Keep the latest commitEdits closure reachable from refs (for the
  // registered flush callback and the click-outside listener).
  commitEditsRef.current = commitEdits;

  // While in EDITING, register a flush callback the parent can call before
  // serializing the estimate, and listen for outside clicks to auto-commit.
  useEffect(() => {
    if (state !== LINE_ITEM_STATE.EDITING) return undefined;

    const lineItemId = item.lineItemId;
    // Capture the shared Map up front so the cleanup uses the same reference
    // even if pendingFlushesRef.current is reassigned later.
    const flushes = pendingFlushesRef?.current || null;
    const flush = () => {
      const fn = commitEditsRef.current;
      return fn ? fn({ skipAIAdjustment: true }) : null;
    };
    if (flushes) flushes.set(lineItemId, flush);

    const handleMouseDown = (event) => {
      const row = editingRowRef.current;
      if (!row) return;
      if (row.contains(event.target)) return;
      flush();
    };
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      if (flushes) flushes.delete(lineItemId);
    };
  }, [state, item.lineItemId, pendingFlushesRef]);

  const hasExplanation = item.aiExplanation || item.userAdjustmentReason || item.costSource === 'uploaded';

  // Mobile sheet renders as an extra <tr> appended to whatever the state
  // returns — the sheet's content is a fixed overlay so its DOM placement
  // doesn't affect layout.
  const mobileSheetRow = mobileSheetOpen ? (
    <tr key={`sheet-${item.lineItemId}`}>
      <td colSpan={8} className="p-0">
        <MobileLineItemSheet
          item={item}
          isNew={isNewItem}
          isCustomMode={isCustomMode}
          categoryCode={categoryCode}
          categoryName={categoryName}
          divisionNumber={divisionNumber}
          estimateId={estimateId}
          projectContext={projectContext}
          excludeEstimateId={excludeEstimateId}
          existingLineItemCodes={existingLineItemCodes}
          onSave={handleMobileSheetSave}
          onCancel={handleMobileSheetCancel}
          onClose={() => setMobileSheetOpen(false)}
        />
      </td>
    </tr>
  ) : null;

  // Render based on current state
  switch (state) {
    case LINE_ITEM_STATE.EDITING:
      return (
        <>
        <tr ref={editingRowRef} className="border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-900/30">
          {!isCustomMode && (
            <td className="py-2 px-4 pl-6">
              {selectedMasterFormat?.code && (
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  {selectedMasterFormat.code.replace(/\s/g, '')}
                </span>
              )}
            </td>
          )}
          <td className="py-2 px-4">
            {isNewItem ? (
              isCustomMode ? (
                // Custom-mode (subcontractor) tenants describe work in their
                // own words; the CSI code is auto-stamped to the tenant's
                // anchor division at the editor level.
                <div>
                  <input
                    type="text"
                    autoFocus
                    value={editValues.description === 'New Line Item' ? '' : editValues.description}
                    onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                    onKeyDown={handleKeyDown}
                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm"
                    placeholder="Describe this line item..."
                  />
                  {validationError && (
                    <div className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {validationError}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <MasterFormatAutocomplete
                    value={selectedMasterFormat}
                    onChange={(selected) => {
                      setSelectedMasterFormat(selected);
                      if (selected) {
                        setValidationError('');
                        setEditValues({ ...editValues, description: selected.title, masterformatCode: selected.code });
                        // Focus on quantity input after selection (but don't auto-select)
                        setTimeout(() => {
                          if (quantityInputRef.current) {
                            quantityInputRef.current.focus();
                            // Removed: quantityInputRef.current.select(); - don't auto-select for better UX
                          }
                        }, 0);
                      }
                    }}
                    placeholder="Search or select a line item..."
                    required
                    divisionFilter={divisionNumber}
                    autoFocus={isNewItem}
                    showInitialList={true}
                    showDivisionHeaders={false}
                    excludeCodes={existingLineItemCodes}
                  />
                  {validationError && (
                    <div className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {validationError}
                    </div>
                  )}
                </div>
              )
            ) : (
              <span className="text-gray-700 dark:text-gray-200">{item.description}</span>
            )}
          </td>
          <td className="py-2 px-4">
            <input
              ref={quantityInputRef}
              type="number"
              value={editValues.quantity}
              onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
              placeholder="Qty"
            />
          </td>
          <td className="py-2 px-4">
            <input
              ref={unitInputRef}
              type="text"
              value={editValues.unit}
              onChange={(e) => setEditValues({ ...editValues, unit: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm"
              placeholder="Unit"
            />
          </td>
          <td className="py-2 px-4">
            {isNewItem && editMode === 'unitCost' ? (
              // Manual unit-cost entry for new items. User can also click
              // the Total cell to enter that instead and have the unit cost
              // back-calculated. AI button in actions cell is still available.
              <input
                ref={unitCostInputRef}
                type="number"
                value={editValues.unitCost}
                onChange={(e) => setEditValues({ ...editValues, unitCost: e.target.value })}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
                placeholder="Unit Cost"
              />
            ) : isNewItem ? (
              // editMode === 'total' for a new item — show computed unit cost.
              <span
                className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right italic"
                onClick={() => {
                  setEditMode('unitCost');
                  setTimeout(() => unitCostInputRef.current?.focus(), 0);
                }}
                title="Click to edit unit cost instead"
              >
                {editValues.quantity && editValues.totalCost
                  ? formatUnitCost(parseFloat(editValues.totalCost) / parseFloat(editValues.quantity))
                  : '—'}
              </span>
            ) : categoryCode === '00' && editMode === 'unitCost' ? (
              // Fee category: show percentage input in unit cost column (editable)
              <div className="relative">
                <input
                  ref={unitCostInputRef}
                  type="number"
                  value={editValues.totalCost}
                  onChange={(e) => setEditValues({
                    ...editValues,
                    totalCost: e.target.value,
                    feeDollarAmount: undefined // Clear dollar amount when editing percentage directly
                  })}
                  onKeyDown={handleKeyDown}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right pr-6"
                  placeholder="%"
                  step="0.1"
                />
                <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-sm">%</span>
              </div>
            ) : categoryCode === '00' ? (
              // Fee category: show percentage as clickable span (when editing total)
              <span
                className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right italic"
                onClick={() => {
                  setEditMode('unitCost');
                  setEditValues(prev => ({ ...prev, feeDollarAmount: undefined }));
                  setTimeout(() => unitCostInputRef.current?.focus(), 0);
                }}
                title="Click to edit percentage instead"
              >
                {editValues.totalCost || item.totalCost}%
              </span>
            ) : editMode === 'unitCost' ? (
              <input
                ref={unitCostInputRef}
                type="number"
                value={editValues.unitCost}
                onChange={(e) => setEditValues({ ...editValues, unitCost: e.target.value })}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
                placeholder="Unit Cost"
              />
            ) : (
              <span
                className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right italic"
                onClick={() => {
                  setEditMode('unitCost');
                  setTimeout(() => unitCostInputRef.current?.focus(), 0);
                }}
                title="Click to edit unit cost instead"
              >
                {editValues.quantity && editValues.totalCost
                  ? formatUnitCost(parseFloat(editValues.totalCost) / parseFloat(editValues.quantity))
                  : item.unitCost ? formatUnitCostWithUnit(item.unitCost, item.unit) : '—'}
              </span>
            )}
          </td>
          <td className="py-2 px-4">
            {isNewItem && editMode === 'total' ? (
              <input
                ref={totalCostInputRef}
                type="number"
                value={editValues.totalCost}
                onChange={(e) => setEditValues({ ...editValues, totalCost: e.target.value })}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
                placeholder="Total"
              />
            ) : isNewItem ? (
              <span
                className="text-gray-700 dark:text-gray-200 text-sm cursor-pointer hover:text-blue-600 block text-right"
                onClick={() => {
                  setEditMode('total');
                  const currentTotal = editValues.quantity && editValues.unitCost
                    ? parseFloat(editValues.quantity) * parseFloat(editValues.unitCost)
                    : '';
                  setEditValues(prev => ({ ...prev, totalCost: currentTotal }));
                  setTimeout(() => totalCostInputRef.current?.focus(), 0);
                }}
                title="Click to enter total instead"
              >
                {editValues.quantity && editValues.unitCost
                  ? formatCurrency(parseFloat(editValues.quantity) * parseFloat(editValues.unitCost))
                  : '—'}
              </span>
            ) : categoryCode === '00' && editMode === 'total' ? (
              // Fee category: editable dollar amount that calculates percentage
              <div className="relative">
                <span className="absolute left-2 top-1.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                <input
                  ref={totalCostInputRef}
                  type="number"
                  value={editValues.feeDollarAmount !== undefined
                    ? editValues.feeDollarAmount
                    : (editValues.totalCost
                        ? Math.round(baseTotalCost * (parseFloat(editValues.totalCost) / 100))
                        : Math.round(baseTotalCost * (item.totalCost / 100)))}
                  onChange={(e) => {
                    const dollarAmount = e.target.value;
                    // Calculate percentage from dollar amount
                    const percentage = baseTotalCost > 0
                      ? (parseFloat(dollarAmount) / baseTotalCost) * 100
                      : 0;
                    setEditValues({
                      ...editValues,
                      totalCost: percentage.toFixed(2),
                      feeDollarAmount: dollarAmount
                    });
                  }}
                  onKeyDown={handleKeyDown}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right pl-6"
                  placeholder="Amount"
                />
              </div>
            ) : categoryCode === '00' ? (
              // Fee category: show dollar amount as clickable span (when editing percentage)
              <span
                className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right italic"
                onClick={() => {
                  setEditMode('total');
                  const currentDollarAmount = editValues.totalCost
                    ? Math.round(baseTotalCost * (parseFloat(editValues.totalCost) / 100))
                    : Math.round(baseTotalCost * (item.totalCost / 100));
                  setEditValues(prev => ({ ...prev, feeDollarAmount: currentDollarAmount }));
                  setTimeout(() => totalCostInputRef.current?.focus(), 0);
                }}
                title="Click to edit dollar amount instead"
              >
                {formatCurrency(editValues.totalCost
                  ? baseTotalCost * (parseFloat(editValues.totalCost) / 100)
                  : baseTotalCost * (item.totalCost / 100))}
              </span>
            ) : editMode === 'total' ? (
              <input
                ref={totalCostInputRef}
                type="number"
                value={editValues.totalCost}
                onChange={(e) => setEditValues({ ...editValues, totalCost: e.target.value })}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
                placeholder="Total"
              />
            ) : (
              <span
                className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right italic"
                onClick={() => {
                  setEditMode('total');
                  const currentTotal = editValues.quantity && editValues.unitCost
                    ? parseFloat(editValues.quantity) * parseFloat(editValues.unitCost)
                    : item.totalCost || '';
                  setEditValues(prev => ({ ...prev, totalCost: currentTotal }));
                  setTimeout(() => totalCostInputRef.current?.focus(), 0);
                }}
                title="Click to edit total instead"
              >
                {editValues.quantity && editValues.unitCost
                  ? formatCurrency(parseFloat(editValues.quantity) * parseFloat(editValues.unitCost))
                  : formatCurrency(item.totalCost)}
              </span>
            )}
          </td>
          <td className="py-2 px-4" />
          <td className="py-2 px-4">
            <div className="flex gap-1 justify-end">
              {/* Row edits auto-commit on focus exit (clicking outside the
                  row or pressing Enter). No explicit Save button. */}
              {!hideAIEstimate && (
                <button
                  onClick={handlePredict}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  title="Get AI estimate for this line item"
                >
                  {isNewItem ? 'Estimate' : 'Estimate with AI'}
                </button>
              )}
              <button
                onClick={handleCancel}
                className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
        {mobileSheetRow}
        </>
      );

    case LINE_ITEM_STATE.PREDICTING:
      return (
        <tr className="border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-900/30">
          {!isCustomMode && (
            <td className="py-2 px-4 pl-6">
              {selectedMasterFormat?.code && (
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                  {selectedMasterFormat.code.replace(/\s/g, '')}
                </span>
              )}
            </td>
          )}
          <td className="py-2 px-4 text-gray-700 dark:text-gray-200">{selectedMasterFormat?.title}</td>
          <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm">{editValues.quantity || '—'}</td>
          <td className="py-2 px-4 text-gray-600 dark:text-gray-300 text-sm">{editValues.unit || '—'}</td>
          <td colSpan={4} className="py-2 px-4">
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <Spinner size="sm" />
              <span className="text-sm">Estimating cost...</span>
            </div>
          </td>
        </tr>
      );

    case LINE_ITEM_STATE.REVIEWING:
      const reviewQuantity = aiPrediction?.quantity || (editValues.quantity ? parseFloat(editValues.quantity) : 1);
      const reviewUnit = aiPrediction?.unit || editValues.unit || 'EA';
      const reviewUnitCost = aiPrediction?.unitCost || 0;
      const reviewTotal = reviewQuantity * reviewUnitCost;
      const isFeeReview = categoryCode === '00';

      return (
        <>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-blue-50 dark:bg-blue-900/30">
            {!isCustomMode && (
              <td className="py-2 px-4 pl-6">
                {(selectedMasterFormat?.code || item.masterformatCode) && (
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {(selectedMasterFormat?.code || item.masterformatCode).replace(/\s/g, '')}
                  </span>
                )}
              </td>
            )}
            <td className="py-2 px-4 text-gray-700 dark:text-gray-200">{selectedMasterFormat?.title || item.description}</td>
            <td className="py-2 px-4 text-right text-blue-700 dark:text-blue-300 font-medium text-sm">
              {isFeeReview ? '—' : (reviewQuantity ? formatNumber(reviewQuantity) : '—')}
            </td>
            <td className="py-2 px-4 text-blue-700 dark:text-blue-300 font-medium text-sm">
              {isFeeReview ? '—' : reviewUnit}
            </td>
            <td className="py-2 px-4 text-right text-blue-700 dark:text-blue-300 font-medium">
              {isFeeReview ? `${reviewUnitCost}%` : (reviewUnitCost ? formatCurrency(reviewUnitCost) : '—')}
            </td>
            <td className="py-2 px-4 text-right text-blue-700 dark:text-blue-300 font-bold">
              {isFeeReview ? formatCurrency(baseTotalCost * (reviewUnitCost / 100)) : (reviewTotal ? formatCurrency(reviewTotal) : '—')}
            </td>
            <td className="py-2 px-4">
              <Badge variant={aiPrediction?.estimationMethod === 'fallback' ? 'yellow' : 'blue'} className="text-xs">
                {aiPrediction?.confidenceScore ? `${Math.round(aiPrediction.confidenceScore * 100)}%` : 'AI'}
              </Badge>
            </td>
            <td className="py-2 px-4" />
          </tr>
          {/* Prediction Review Panel */}
          <tr>
            <td colSpan={8} className="p-0">
              <div className="bg-blue-50 dark:bg-blue-900/30 border-t border-b border-blue-200 dark:border-blue-800 p-4">
                {/* AI Explanation */}
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">How this was calculated:</h5>
                  {aiPrediction?.estimationMethod === 'fallback' && (
                    <div className="mb-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-sm text-yellow-800 dark:text-yellow-300">
                      AI estimation was unavailable — this is a fallback estimate based on historical averages.
                    </div>
                  )}
                  <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                    {aiPrediction?.explanation || 'AI estimate based on historical data.'}
                  </p>
                </div>

                {/* Similar Items Table */}
                {similarItems.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Similar Line Items (same MasterFormat code):
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 dark:border-gray-800 rounded">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                          <tr>
                            <th className="px-3 py-2 text-left">Project</th>
                            <th className="px-3 py-2 text-right">Quantity</th>
                            <th className="px-3 py-2 text-right">Unit Cost</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2 text-right">Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {similarItems.map((simItem, idx) => (
                            <tr key={idx} className="border-t border-gray-200 dark:border-gray-800">
                              <td className="px-3 py-2">{simItem.projectName || 'Historical Project'}</td>
                              <td className="px-3 py-2 text-right">
                                {formatNumber(simItem.quantity || 0)} {simItem.unit || ''}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {simItem.unitCost ? `${formatUnitCost(simItem.unitCost)}/${simItem.unit || 'EA'}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCurrency(simItem.totalCost || 0)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {simItem.similarityScore ? `${Math.round(simItem.similarityScore * 100)}%` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Price Range Info */}
                {aiPrediction?.priceRange && (
                  <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium">Price range:</span>{' '}
                    {isFeeReview
                      ? `${aiPrediction.priceRange.min}% - ${aiPrediction.priceRange.max}%`
                      : `${formatCurrency(aiPrediction.priceRange.min)} - ${formatCurrency(aiPrediction.priceRange.max)}`
                    }
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setState(LINE_ITEM_STATE.ADJUSTING)}
                    className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleApprove}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </>
      );

    case LINE_ITEM_STATE.ADJUSTING:
      const adjQty = adjustmentValues.quantity ? parseFloat(adjustmentValues.quantity) : null;
      const adjUnitCost = adjustmentValues.unitCost ? parseFloat(adjustmentValues.unitCost) : null;
      const isFeeAdjust = categoryCode === '00';
      const newTotal = isFeeAdjust
        ? (adjUnitCost ? baseTotalCost * (adjUnitCost / 100) : null)
        : (adjQty && adjUnitCost ? adjQty * adjUnitCost : null);
      const aiTotal = isFeeAdjust
        ? (aiPrediction?.unitCost ? baseTotalCost * (aiPrediction.unitCost / 100) : 0)
        : ((aiPrediction?.quantity || 1) * (aiPrediction?.unitCost || 0));
      const costDiff = aiTotal && newTotal
        ? ((newTotal - aiTotal) / aiTotal * 100)
        : null;

      return (
        <>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-yellow-50 dark:bg-yellow-900/30">
            {!isCustomMode && (
              <td className="py-2 px-4 pl-6">
                {(selectedMasterFormat?.code || item.masterformatCode) && (
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {(selectedMasterFormat?.code || item.masterformatCode).replace(/\s/g, '')}
                  </span>
                )}
              </td>
            )}
            <td className="py-2 px-4 text-gray-700 dark:text-gray-200">{selectedMasterFormat?.title || item.description}</td>
            <td className="py-2 px-4">
              {isFeeAdjust ? (
                <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
              ) : (
                <input
                  type="number"
                  value={adjustmentValues.quantity}
                  onChange={(e) => setAdjustmentValues({ ...adjustmentValues, quantity: e.target.value })}
                  className="w-full border border-yellow-400 rounded px-2 py-1 text-sm text-right bg-white dark:bg-gray-900"
                  step="0.01"
                  autoFocus
                />
              )}
            </td>
            <td className="py-2 px-4">
              {isFeeAdjust ? (
                <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
              ) : (
                <input
                  type="text"
                  value={adjustmentValues.unit}
                  onChange={(e) => setAdjustmentValues({ ...adjustmentValues, unit: e.target.value })}
                  className="w-20 border border-yellow-400 rounded px-2 py-1 text-sm bg-white dark:bg-gray-900"
                />
              )}
            </td>
            <td className="py-2 px-4">
              <div className="relative">
                {isFeeAdjust ? (
                  <>
                    <input
                      type="number"
                      value={adjustmentValues.unitCost}
                      onChange={(e) => setAdjustmentValues({ ...adjustmentValues, unitCost: e.target.value })}
                      className="w-full border border-yellow-400 rounded px-2 pr-6 py-1 text-sm text-right bg-white dark:bg-gray-900"
                      step="0.1"
                      autoFocus
                    />
                    <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-sm">%</span>
                  </>
                ) : (
                  <>
                    <span className="absolute left-2 top-1.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      value={adjustmentValues.unitCost}
                      onChange={(e) => setAdjustmentValues({ ...adjustmentValues, unitCost: e.target.value })}
                      className="w-full border border-yellow-400 rounded pl-6 pr-2 py-1 text-sm text-right bg-white dark:bg-gray-900"
                      step="0.01"
                    />
                  </>
                )}
              </div>
            </td>
            <td className="py-2 px-4 text-right text-yellow-700 dark:text-yellow-300 font-bold">
              {newTotal ? formatCurrency(newTotal) : '—'}
            </td>
            <td className="py-2 px-4">
              <Badge variant="yellow" className="text-xs">Adjusting</Badge>
            </td>
            <td className="py-2 px-4" />
          </tr>
          {/* Adjustment Panel */}
          <tr>
            <td colSpan={8} className="p-0">
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border-t border-b border-yellow-200 dark:border-yellow-800 p-4">
                <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-3">Adjust AI Estimate</h4>

                {/* Show AI original estimate breakdown */}
                {aiPrediction && (
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 mb-4 flex justify-between items-center">
                    <span className="text-sm text-blue-800 dark:text-blue-300">AI Estimate:</span>
                    <span className="font-semibold text-blue-900 dark:text-blue-200">
                      {isFeeAdjust
                        ? (aiPrediction.unitCost
                          ? `${aiPrediction.unitCost}% = ${formatCurrency(baseTotalCost * (aiPrediction.unitCost / 100))}`
                          : '—')
                        : (aiPrediction.quantity && aiPrediction.unitCost
                          ? `${formatNumber(aiPrediction.quantity)} ${aiPrediction.unit || 'EA'} × ${formatUnitCost(aiPrediction.unitCost)}/${aiPrediction.unit || 'EA'} = ${formatCurrency(aiPrediction.quantity * aiPrediction.unitCost)}`
                          : '—')
                      }
                    </span>
                  </div>
                )}

                {/* Cost difference indicator */}
                {costDiff !== null && (
                  <p className={`text-sm mb-3 ${costDiff >= 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                    Your adjustment: {costDiff >= 0 ? '+' : ''}{costDiff.toFixed(1)}% from AI estimate
                  </p>
                )}

                {/* Explanation textarea */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Why are you adjusting? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={adjustmentValues.reason}
                    onChange={(e) => {
                      setAdjustmentValues({ ...adjustmentValues, reason: e.target.value });
                      setAdjustmentError('');
                    }}
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g., Market rates have increased, specialized equipment required, site access constraints..."
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Your explanation helps improve future estimates.
                  </p>
                </div>

                {/* Error message */}
                {adjustmentError && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-3 mb-4 text-red-700 dark:text-red-300 text-sm">
                    {adjustmentError}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end mb-4">
                  <button
                    onClick={() => {
                      setAdjustmentError('');
                      setState(LINE_ITEM_STATE.REVIEWING);
                    }}
                    className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleAdjustmentSave}
                    className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    Save Adjustment
                  </button>
                </div>

                {/* AI Explanation */}
                {aiPrediction?.explanation && (
                  <div className="border-t border-yellow-200 dark:border-yellow-800 pt-4 mt-2 mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">How this was calculated:</h5>
                    <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                      {aiPrediction.explanation}
                    </p>
                  </div>
                )}

                {/* Similar Items Table */}
                {similarItems.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Similar Line Items (same MasterFormat code):
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 dark:border-gray-800 rounded">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                          <tr>
                            <th className="px-3 py-2 text-left">Project</th>
                            <th className="px-3 py-2 text-right">Quantity</th>
                            <th className="px-3 py-2 text-right">Unit Cost</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2 text-right">Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {similarItems.map((simItem, idx) => (
                            <tr key={idx} className="border-t border-gray-200 dark:border-gray-800">
                              <td className="px-3 py-2">{simItem.projectName || 'Historical Project'}</td>
                              <td className="px-3 py-2 text-right">
                                {formatNumber(simItem.quantity || 0)} {simItem.unit || ''}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {simItem.unitCost ? `${formatUnitCost(simItem.unitCost)}/${simItem.unit || 'EA'}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCurrency(simItem.totalCost || 0)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {simItem.similarityScore ? `${Math.round(simItem.similarityScore * 100)}%` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Price Range Info */}
                {aiPrediction?.priceRange && (
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium">Price range:</span>{' '}
                    {formatCurrency(aiPrediction.priceRange.min)} - {formatCurrency(aiPrediction.priceRange.max)}
                  </div>
                )}
              </div>
            </td>
          </tr>
        </>
      );

    case LINE_ITEM_STATE.VIEW:
    default:
      return (
        <>
          <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 group cursor-pointer">
            {!isCustomMode && (
              <td className="hidden md:table-cell py-2 px-4 pl-6" onClick={() => handleCellClick('quantity')}>
                {item.masterformatCode && (
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {item.masterformatCode.replace(/\s/g, '')}
                  </span>
                )}
              </td>
            )}
            <td className="py-2 px-4 align-top" onClick={() => handleCellClick('quantity')}>
              <div className="flex items-start gap-2">
                <span className="text-gray-700 dark:text-gray-200 break-words min-w-0">{item.description}</span>
                {(item.isUnmapped || !item.masterformatCode) && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 flex-shrink-0">
                    Unmapped
                  </span>
                )}
                {hasExplanation && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowExplanation(!showExplanation);
                    }}
                    className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                    title="View cost explanation"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Mobile-only inline meta: qty/unit/unitCost/source/code are hidden on phones. */}
              <div className="md:hidden mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                {!isCustomMode && item.masterformatCode && (
                  <span className="font-mono">{item.masterformatCode.replace(/\s/g, '')}</span>
                )}
                {(item.quantity || item.unit) && (
                  <span>{item.quantity ? formatNumber(item.quantity) : '—'} {item.unit || ''}</span>
                )}
                {categoryCode !== '00' && item.unitCost && (
                  <span>@ {formatUnitCostWithUnit(item.unitCost, item.unit)}</span>
                )}
                <CostSourceBadge
                  source={item.costSource || 'manual'}
                  confidenceScore={item.aiConfidenceScore}
                />
              </div>
            </td>
            <td className="hidden md:table-cell py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm" onClick={() => handleCellClick('quantity')}>
              {item.quantity ? formatNumber(item.quantity) : '—'}
            </td>
            <td className="hidden md:table-cell py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm" onClick={() => handleCellClick('unit')}>
              {item.unit || '—'}
            </td>
            <td className="hidden md:table-cell py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm" onClick={() => handleCellClick('unitCost')}>
              {categoryCode === '00' ? `${item.totalCost}%` : (item.unitCost ? formatUnitCostWithUnit(item.unitCost, item.unit) : '—')}
            </td>
            <td className="py-2 px-4 text-right text-gray-700 dark:text-gray-200 whitespace-nowrap align-top" onClick={() => handleCellClick('total')}>
              {categoryCode === '00' ? formatCurrency(baseTotalCost * (item.totalCost / 100)) : formatCurrency(item.totalCost)}
            </td>
            <td className="hidden md:table-cell py-2 px-4" onClick={(e) => {
              if (hasExplanation) {
                e.stopPropagation();
                setShowExplanation(!showExplanation);
              } else {
                handleCellClick('quantity');
              }
            }}>
              <CostSourceBadge
                source={item.costSource || 'manual'}
                confidenceScore={item.aiConfidenceScore}
              />
            </td>
            <td className="py-2 px-2 md:px-4 align-top">
              {/* Always-visible on touch; faded-until-hover on desktop. */}
              <div className="flex gap-1 justify-end md:opacity-0 md:group-hover:opacity-100 md:transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  aria-label="Delete line item"
                  className="px-2 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                >
                  <span className="hidden md:inline">Delete</span>
                  <svg className="md:hidden w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </td>
          </tr>
          {showExplanation && hasExplanation && (
            <tr>
              <td colSpan={8} className="px-4">
                <ExplanationPanel
                  item={item}
                  onClose={() => setShowExplanation(false)}
                  onReclassify={async ({ sourceDescription, masterformatCode, masterformatTitle, persistMapping }) => {
                    try {
                      await estimatesApi.updateLineItem(estimateId, item.lineItemId, {
                        masterformatCode,
                        masterformatTitle,
                        persistMapping,
                        sourceDescription,
                      });
                      onUpdate({
                        ...item,
                        masterformatCode,
                        description: masterformatTitle || item.description,
                      });
                    } catch (err) {
                      console.error('Reclassify failed:', err);
                      showError('Failed to reclassify line item. Please try again.');
                    }
                  }}
                />
              </td>
            </tr>
          )}
          {mobileSheetRow}
        </>
      );
  }
}

function CategoryEditor({ category, onUpdate, onDelete, estimateId, projectContext, onRequestAIEstimate, isNewCategory = false, baseTotalCost = 0, existingCategoryCodes = [], hideAIEstimate = false, isCustomMode = false, pendingFlushesRef = null }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingName, setIsEditingName] = useState(isNewCategory || category.categoryName === 'New Category');
  const [categoryName, setCategoryName] = useState(category.categoryName);
  // Removed: const [isEstimating, setIsEstimating] = useState(false); - bulk AI estimation removed
  const [selectedDivision, setSelectedDivision] = useState(
    category.categoryCode && category.divisionNumber
      ? { code: category.categoryCode, title: category.categoryName, divisionNumber: category.divisionNumber }
      : null
  );

  const handleAddLineItem = () => {
    const newItem = {
      lineItemId: generateTempId(),
      description: 'New Line Item',
      quantity: 0,
      unit: 'LS',
      unitCost: 0,
      totalCost: 0,
      costSource: 'manual',
    };
    onUpdate({
      ...category,
      lineItems: [...(category.lineItems || []), newItem],
    });
  };

  const handleUpdateLineItem = (updatedItem) => {
    onUpdate({
      ...category,
      lineItems: category.lineItems.map((item) =>
        item.lineItemId === updatedItem.lineItemId ? updatedItem : item
      ),
    });
  };

  const handleDeleteLineItem = (lineItemId) => {
    onUpdate({
      ...category,
      lineItems: category.lineItems.filter((item) => item.lineItemId !== lineItemId),
    });
  };

  const handleSaveName = () => {
    if (selectedDivision) {
      onUpdate({
        ...category,
        categoryName: selectedDivision.title,
        categoryCode: selectedDivision.code,
        divisionNumber: selectedDivision.divisionNumber,
      });
    } else {
      onUpdate({ ...category, categoryName });
    }
    setIsEditingName(false);
  };

  // Handle division selection from autocomplete
  const handleDivisionSelect = (selected) => {
    setSelectedDivision(selected);
    if (selected) {
      setCategoryName(selected.title);
      // Auto-save when a division is selected for a new category
      if (isNewCategory || category.categoryName === 'New Category') {
        onUpdate({
          ...category,
          categoryName: selected.title,
          categoryCode: selected.code,
          divisionNumber: selected.divisionNumber,
        });
        setIsEditingName(false);
      }
    }
  };

  // Removed: handleEstimateAllLineItems - bulk AI estimation removed
  // Individual line item estimation now happens via "Estimate with AI" button during editing

  const categoryTotal = (category.lineItems || []).reduce(
    (sum, item) => sum + (item.totalCost || 0),
    0
  );

  const aiEstimatedCount = (category.lineItems || []).filter(
    (item) => item.costSource === 'ai' || item.aiEstimatedCost
  ).length;

  return (
    <Card className="mb-4">
      <CardHeader className="bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100"
            >
              <svg
                className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {isEditingName ? (
              <div className="flex items-center gap-2 flex-1">
                <div className="flex-1 max-w-md">
                  <MasterFormatAutocomplete
                    value={selectedDivision}
                    onChange={handleDivisionSelect}
                    onSelect={handleDivisionSelect}
                    placeholder="Search or select a category..."
                    levelFilter={1}
                    autoFocus
                    showInitialList={true}
                    showDivisionHeaders={false}
                    excludeCodes={existingCategoryCodes}
                  />
                </div>
                <button
                  onClick={handleSaveName}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setCategoryName(category.categoryName);
                    setSelectedDivision(
                      category.categoryCode && category.divisionNumber
                        ? { code: category.categoryCode, title: category.categoryName, divisionNumber: category.divisionNumber }
                        : null
                    );
                    setIsEditingName(false);
                  }}
                  className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">
                  {category.divisionNumber !== undefined && category.divisionNumber !== null ? `${String(category.divisionNumber).padStart(2, '0')}. ` : ''}
                  {category.categoryName}
                </CardTitle>
                <button
                  onClick={() => setIsEditingName(true)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {category.lineItems?.length || 0} items
              {aiEstimatedCount > 0 && (
                <span className="ml-1 text-blue-600 dark:text-blue-400">({aiEstimatedCount} AI)</span>
              )}
              {' '}&bull; {category.categoryCode === '00'
                ? formatCurrency(baseTotalCost * (categoryTotal / 100))
                : formatCurrency(categoryTotal)}
            </span>
            {/* AI Estimate button removed - individual line item estimation now happens during editing */}
            <button
              onClick={onDelete}
              aria-label="Delete category"
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              <span className="hidden sm:inline">Delete Category</span>
              <svg className="sm:hidden w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <div className="md:overflow-visible overflow-x-auto -mx-4 sm:-mx-6 md:mx-0 px-4 sm:px-6 md:px-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  {!isCustomMode && (
                    <th className="hidden md:table-cell text-left py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-24">
                      Code
                    </th>
                  )}
                  <th className="text-left py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
                    Description
                  </th>
                  <th className="hidden md:table-cell text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-32">
                    Quantity
                  </th>
                  <th className="hidden md:table-cell text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-20">
                    Unit
                  </th>
                  <th className="hidden md:table-cell text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-28">
                    Unit Cost
                  </th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-32">
                    Total
                  </th>
                  <th className="hidden md:table-cell text-center py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-28">
                    Source
                  </th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-20 md:w-32">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(category.lineItems || []).map((item) => (
                  <LineItemEditor
                    key={item.lineItemId}
                    item={item}
                    onUpdate={handleUpdateLineItem}
                    onDelete={() => handleDeleteLineItem(item.lineItemId)}
                    estimateId={estimateId}
                    divisionNumber={category.divisionNumber}
                    projectContext={projectContext}
                    categoryName={category.categoryName}
                    categoryCode={category.categoryCode}
                    excludeEstimateId={estimateId}
                    baseTotalCost={baseTotalCost}
                    existingLineItemCodes={(category.lineItems || [])
                      .filter(li => li.masterformatCode && li.lineItemId !== item.lineItemId)
                      .map(li => li.masterformatCode)}
                    hideAIEstimate={hideAIEstimate}
                    isCustomMode={isCustomMode}
                    pendingFlushesRef={pendingFlushesRef}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={handleAddLineItem}>
              + Add Line Item
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// Legacy view line item row with editable numeric fields (description is read-only)
function LegacyLineItemRow({ item, baseTotalCost, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    quantity: item.quantity || '',
    unit: item.unit || '',
    unitCost: item.unitCost || '',
    totalCost: item.totalCost || '',
  });
  const [editMode, setEditMode] = useState('unitCost'); // 'unitCost' or 'total'

  const isFeeCategory = item.originalCategoryCode === '00';

  const handleSave = () => {
    let quantity, unitCost, totalCost;

    if (isFeeCategory) {
      // Fee category: totalCost stores the percentage value
      totalCost = editValues.totalCost ? parseFloat(editValues.totalCost) : item.totalCost;
      quantity = null;
      unitCost = null;
    } else {
      quantity = editValues.quantity ? parseFloat(editValues.quantity) : item.quantity;

      if (editMode === 'total') {
        totalCost = editValues.totalCost ? parseFloat(editValues.totalCost) : item.totalCost;
        unitCost = quantity && totalCost ? totalCost / quantity : item.unitCost;
      } else {
        unitCost = editValues.unitCost ? parseFloat(editValues.unitCost) : item.unitCost;
        totalCost = quantity && unitCost ? quantity * unitCost : item.totalCost;
      }
    }

    onUpdate({
      ...item,
      quantity,
      unit: isFeeCategory ? null : editValues.unit,
      unitCost,
      totalCost,
      // Keep existing cost source info but mark as adjusted/manual if values changed
      costSource: item.costSource === 'ai' && totalCost !== item.totalCost
        ? 'user_adjusted'
        : item.costSource === 'template' && totalCost !== item.totalCost
        ? 'manual'
        : item.costSource,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValues({
      quantity: item.quantity || '',
      unit: item.unit || '',
      unitCost: item.unitCost || '',
      totalCost: item.totalCost || '',
    });
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <tr className="border-b border-gray-100 dark:border-gray-800 bg-blue-50 dark:bg-blue-900/30">
        <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">
          {item.originalCategoryCode && `[${item.originalCategoryCode.substring(0, 2)}]`}
        </td>
        <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-200">{item.description}</td>
        <td className="py-2 px-3">
          {isFeeCategory ? (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          ) : (
            <input
              type="number"
              value={editValues.quantity}
              onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
              autoFocus
            />
          )}
        </td>
        <td className="py-2 px-3">
          {isFeeCategory ? (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          ) : (
            <input
              type="text"
              value={editValues.unit}
              onChange={(e) => setEditValues({ ...editValues, unit: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm"
            />
          )}
        </td>
        <td className="py-2 px-3">
          {isFeeCategory ? (
            <div className="relative">
              <input
                type="number"
                value={editValues.totalCost}
                onChange={(e) => setEditValues({ ...editValues, totalCost: e.target.value })}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right pr-6"
                step="0.1"
              />
              <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-sm">%</span>
            </div>
          ) : editMode === 'unitCost' ? (
            <input
              type="number"
              value={editValues.unitCost}
              onChange={(e) => setEditValues({ ...editValues, unitCost: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
            />
          ) : (
            <span
              className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right"
              onClick={() => setEditMode('unitCost')}
            >
              {editValues.quantity && editValues.totalCost
                ? formatUnitCost(parseFloat(editValues.totalCost) / parseFloat(editValues.quantity))
                : formatUnitCostWithUnit(item.unitCost, item.unit)}
            </span>
          )}
        </td>
        <td className="py-2 px-3">
          {isFeeCategory ? (
            <span className="text-gray-700 dark:text-gray-200 text-sm text-right block">
              {formatCurrency(baseTotalCost * (parseFloat(editValues.totalCost || item.totalCost) / 100))}
            </span>
          ) : editMode === 'total' ? (
            <input
              type="number"
              value={editValues.totalCost}
              onChange={(e) => setEditValues({ ...editValues, totalCost: e.target.value })}
              onKeyDown={handleKeyDown}
              className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-right"
            />
          ) : (
            <span
              className="text-gray-400 dark:text-gray-500 text-sm cursor-pointer hover:text-blue-600 block text-right"
              onClick={() => {
                setEditMode('total');
                const currentTotal = editValues.quantity && editValues.unitCost
                  ? parseFloat(editValues.quantity) * parseFloat(editValues.unitCost)
                  : item.totalCost || '';
                setEditValues((prev) => ({ ...prev, totalCost: currentTotal }));
              }}
            >
              {editValues.quantity && editValues.unitCost
                ? formatCurrency(parseFloat(editValues.quantity) * parseFloat(editValues.unitCost))
                : formatCurrency(item.totalCost)}
            </span>
          )}
        </td>
        <td className="py-2 px-3">
          <CostSourceBadge source={item.costSource || 'manual'} confidenceScore={item.aiConfidenceScore} />
        </td>
        <td className="py-2 px-3">
          <div className="flex gap-1 justify-end">
            <button
              onClick={handleSave}
              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr
      className="border-b border-gray-100 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
      onClick={() => setIsEditing(true)}
    >
      <td className="hidden md:table-cell py-2 px-3 text-sm text-gray-500 dark:text-gray-400">
        {item.originalCategoryCode && `[${item.originalCategoryCode.substring(0, 2)}]`}
      </td>
      <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-200 align-top">
        <span className="break-words">{item.description}</span>
        <div className="md:hidden mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          {item.originalCategoryCode && <span>[{item.originalCategoryCode.substring(0, 2)}]</span>}
          {(item.quantity || item.unit) && (
            <span>{item.quantity ? formatNumber(item.quantity) : '—'} {item.unit || ''}</span>
          )}
          {!isFeeCategory && item.unitCost && <span>@ {formatUnitCostWithUnit(item.unitCost, item.unit)}</span>}
          <CostSourceBadge source={item.costSource || 'manual'} confidenceScore={item.aiConfidenceScore} />
        </div>
      </td>
      <td className="hidden md:table-cell py-2 px-3 text-sm text-right text-gray-600 dark:text-gray-300">
        {item.quantity ? formatNumber(item.quantity) : '—'}
      </td>
      <td className="hidden md:table-cell py-2 px-3 text-sm text-gray-600 dark:text-gray-300">{item.unit || '—'}</td>
      <td className="hidden md:table-cell py-2 px-3 text-sm text-right text-gray-600 dark:text-gray-300">
        {isFeeCategory ? `${item.totalCost}%` : (item.unitCost ? formatUnitCostWithUnit(item.unitCost, item.unit) : '—')}
      </td>
      <td className="py-2 px-3 text-sm text-right text-gray-700 dark:text-gray-200 whitespace-nowrap align-top">
        {isFeeCategory ? formatCurrency(baseTotalCost * (item.totalCost / 100)) : formatCurrency(item.totalCost)}
      </td>
      <td className="hidden md:table-cell py-2 px-3">
        <CostSourceBadge source={item.costSource || 'manual'} confidenceScore={item.aiConfidenceScore} />
      </td>
      <td className="hidden md:table-cell py-2 px-3 text-right">
        <span className="text-xs text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100">Click to edit</span>
      </td>
    </tr>
  );
}

// Legacy category display with expandable line items table
function LegacyCategoryDisplay({ legacyCategory, baseTotalCost, onUpdateLineItem }) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card className="border-gray-200 dark:border-gray-800">
      <CardHeader
        className="bg-gray-50 dark:bg-gray-900 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <div>
              <CardTitle className="text-base">
                {legacyCategory.categoryCode}. {legacyCategory.categoryName}
              </CardTitle>
              {legacyCategory.isLegacyGroup && getLegacyDivisionSubtitle(legacyCategory.legacyDivisionNumber) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {getLegacyDivisionSubtitle(legacyCategory.legacyDivisionNumber)}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrency(legacyCategory.subtotal)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {legacyCategory.lineItems?.length || 0} line items
            </p>
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="hidden md:table-cell py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-12">Div</th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Description</th>
                  <th className="hidden md:table-cell py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-20">Qty</th>
                  <th className="hidden md:table-cell py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-16">Unit</th>
                  <th className="hidden md:table-cell py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Unit Cost</th>
                  <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-28">Total</th>
                  <th className="hidden md:table-cell py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Source</th>
                  <th className="hidden md:table-cell py-2 px-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {legacyCategory.lineItems?.map((item) => (
                  <LegacyLineItemRow
                    key={item.lineItemId}
                    item={item}
                    baseTotalCost={baseTotalCost}
                    onUpdate={onUpdateLineItem}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// Unmapped item resolution component
function UnmappedItemResolution({ item, onResolve, existingItems }) {
  const [action, setAction] = useState(null); // 'reassign' | 'merge' | 'exclude'
  const [selectedCode, setSelectedCode] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [persistMapping, setPersistMapping] = useState(true);


  return (
    <div className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-white dark:bg-gray-900">
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{item.description}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Category: {item.categoryName} ({item.categoryCode}) | Cost: {formatCurrency(item.totalCost)}
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => setAction('reassign')}
          className={`px-3 py-1.5 text-sm rounded border transition-colors ${
            action === 'reassign'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          Reassign Code
        </button>
        <button
          onClick={() => setAction('merge')}
          className={`px-3 py-1.5 text-sm rounded border transition-colors ${
            action === 'merge'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          Merge Into Existing
        </button>
        <button
          onClick={() => setAction('exclude')}
          className={`px-3 py-1.5 text-sm rounded border transition-colors ${
            action === 'exclude'
              ? 'bg-red-600 text-white border-red-600'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
        >
          Exclude
        </button>
      </div>

      {/* Reassign: show MasterFormatAutocomplete */}
      {action === 'reassign' && (
        <div className="mt-2 space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Select MasterFormat code:</label>
          <MasterFormatAutocomplete
            value={selectedCode}
            onChange={(val) => setSelectedCode(val)}
            autoFocus
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={persistMapping}
              onChange={(e) => setPersistMapping(e.target.checked)}
            />
            Remember this mapping for future estimates
          </label>
          <Button
            size="sm"
            disabled={!selectedCode}
            onClick={() => onResolve(item, 'reassign', { code: selectedCode, persistMapping })}
          >
            Apply
          </Button>
        </div>
      )}

      {/* Merge: show dropdown of existing mapped items in same category */}
      {action === 'merge' && (
        <div className="mt-2 space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Merge cost into:</label>
          {existingItems.length > 0 ? (
            <>
              <select
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select item to merge into...</option>
                {existingItems.map(i => (
                  <option key={i.lineItemId} value={i.lineItemId}>
                    {i.description} ({formatCurrency(i.totalCost)})
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={!mergeTarget}
                onClick={() => onResolve(item, 'merge', { targetId: mergeTarget })}
              >
                Merge
              </Button>
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No mapped items in this category to merge into.</p>
          )}
        </div>
      )}

      {/* Exclude: confirm */}
      {action === 'exclude' && (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-red-700 dark:text-red-300">
            This item ({formatCurrency(item.totalCost)}) will be removed from the estimate.
          </p>
          <Button
            size="sm"
            variant="danger"
            onClick={() => onResolve(item, 'exclude', {})}
          >
            Confirm Exclude
          </Button>
        </div>
      )}
    </div>
  );
}

export function EstimateTreeEditor({ initialEstimate, projectName, clientName, location, onSave, onCancel, onNext, initialViewMode = 'modern', hideAIEstimate = false, hideDocuments = false }) {
  const { showError } = useToast();
  const [estimate, setEstimate] = useState(initialEstimate);
  // For new estimates (no estimateId), allow saving without changes
  const isNewEstimate = !initialEstimate.estimateId;
  const [hasChanges, setHasChanges] = useState(isNewEstimate);
  const [isSaving, setIsSaving] = useState(false);
  // Delete category confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  // Unmapped items resolution panel
  const [showUnmappedPanel, setShowUnmappedPanel] = useState(false);

  // Registry of pending row-level edits. Each LineItemEditor in EDITING
  // state registers a flush() that, when called, commits its local editValues
  // up to the parent's estimate state. Top-level Save calls all of these
  // before serializing, so in-progress row edits are never silently dropped.
  const pendingFlushesRef = useRef(new Map());

  // Tenant vocabulary preferences. Subcontractors (custom mode) work in a
  // single CSI division and use their own vocabulary, so the CSI-strict
  // "unmapped" validation and the multi-division UI affordances are hidden
  // for them.
  const tenantPrefs = useTenantPreferences();
  const prefsLoaded =
    tenantPrefs.defaultVocabularyMode !== null || tenantPrefs.businessType !== null;
  const isCustomMode = prefsLoaded && tenantPrefs.defaultVocabularyMode === 'custom';
  const anchorDivision = tenantPrefs.subcontractorAnchorDivision || null;
  const shouldAutoStampAnchor = isCustomMode && !!anchorDivision;

  // Compute unmapped line items across all categories
  const unmappedItems = useMemo(() => {
    const items = [];
    (estimate.categories || []).forEach(cat => {
      (cat.lineItems || []).forEach(item => {
        if (item.isUnmapped || !item.masterformatCode) {
          items.push({
            ...item,
            categoryId: cat.categoryId,
            categoryName: cat.categoryName,
            categoryCode: cat.categoryCode,
          });
        }
      });
    });
    return items;
  }, [estimate.categories]);

  const hasUnmappedItems = unmappedItems.length > 0 && !isCustomMode;

  // Vocabulary view toggle (Custom / CSI 50 / CSI 15). When the caller
  // doesn't explicitly pass an initialViewMode, fall back to the tenant's
  // saved default. The signature default ('modern') stays as the lowest
  // priority so existing callers don't change behavior unexpectedly.
  const resolvedInitialViewMode = initialViewMode === 'modern'
    ? (tenantPrefs.defaultVocabularyMode || 'modern')
    : initialViewMode;
  const {
    viewMode: masterFormatViewMode,
    setViewMode: setMasterFormatViewMode,
    displayCategories,
    isLegacyView,
  } = useMasterFormatView(estimate?.categories, resolvedInitialViewMode);

  // Project context for AI estimation
  const projectContext = {
    jobType: estimate.jobType || initialEstimate.jobType,
    squareFootage: estimate.squareFootage || initialEstimate.squareFootage,
    location: estimate.location || initialEstimate.location,
    prevailingWage: estimate.prevailingWage || initialEstimate.prevailingWage,
  };

  useEffect(() => {
    setEstimate(initialEstimate);
  }, [initialEstimate]);

  // For subcontractor (custom-mode) tenants: stamp any line item missing
  // a masterformatCode with the tenant's anchor division, and create a
  // single default "Line Items" category if the estimate has none yet.
  // The mutated short-circuit keeps this from looping.
  useEffect(() => {
    if (!shouldAutoStampAnchor) return;
    const anchor = anchorDivision;
    const divisionNumber = parseInt(anchor, 10) || null;

    setEstimate(prev => {
      let mutated = false;
      let categories = prev.categories || [];

      if (categories.length === 0) {
        categories = [{
          categoryId: generateTempId(),
          categoryCode: anchor,
          categoryName: 'Line Items',
          divisionNumber,
          subtotal: 0,
          lineItems: [],
        }];
        mutated = true;
      }

      const nextCategories = categories.map(cat => {
        let catMutated = false;
        const nextItems = (cat.lineItems || []).map(item => {
          if (!item.masterformatCode || item.isUnmapped) {
            catMutated = true;
            return {
              ...item,
              masterformatCode: item.masterformatCode || anchor,
              isUnmapped: false,
            };
          }
          return item;
        });
        if (!catMutated) return cat;
        mutated = true;
        return { ...cat, lineItems: nextItems };
      });

      if (!mutated) return prev;
      return { ...prev, categories: nextCategories };
    });
  }, [shouldAutoStampAnchor, anchorDivision, estimate.categories]);

  const handleAddCategory = () => {
    const newCategory = {
      categoryId: generateTempId(),
      categoryName: 'New Category',
      categoryCode: '',
      subtotal: 0,
      lineItems: [],
    };
    setEstimate((prev) => ({
      ...prev,
      categories: [...(prev.categories || []), newCategory],
    }));
    setHasChanges(true);
  };

  const handleUpdateCategory = (updatedCategory) => {
    setEstimate((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.categoryId === updatedCategory.categoryId ? updatedCategory : cat
      ),
    }));
    setHasChanges(true);
  };

  const handleDeleteCategory = (categoryId) => {
    setCategoryToDelete(categoryId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteCategory = () => {
    if (categoryToDelete) {
      setEstimate((prev) => ({
        ...prev,
        categories: prev.categories.filter((cat) => cat.categoryId !== categoryToDelete),
      }));
      setHasChanges(true);
    }
    setDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };

  const cancelDeleteCategory = () => {
    setDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };

  // Removed: handleEstimateAllCategories - bulk AI estimation removed
  // Individual line item estimation now happens via "Estimate with AI" button during editing

  const FEE_CATEGORY_CODE = '00';

  const recalculateTotals = (estimateData) => {
    const categories = estimateData.categories.map((cat) => {
      const subtotal = (cat.lineItems || []).reduce(
        (sum, item) => sum + (item.totalCost || 0),
        0
      );
      return { ...cat, subtotal };
    });

    // Calculate base total (excluding fee category)
    const baseTotalCost = categories
      .filter((cat) => cat.categoryCode !== FEE_CATEGORY_CODE)
      .reduce((sum, cat) => sum + cat.subtotal, 0);

    // Find fee percentage if fee category exists
    const feeCategory = categories.find((cat) => cat.categoryCode === FEE_CATEGORY_CODE);
    const feePercentage = feeCategory?.lineItems?.[0]?.totalCost || 0;

    // Calculate fee amount and grand total
    const feeAmount = feePercentage > 0 ? baseTotalCost * (feePercentage / 100) : 0;
    const grandTotal = baseTotalCost + feeAmount;

    // Use grand total as the totalCost (includes fee)
    const totalCost = grandTotal;
    const costPerSf = estimateData.squareFootage
      ? totalCost / estimateData.squareFootage
      : 0;

    // Calculate fee per month if timeline is set
    const feePerMonth =
      feeAmount > 0 && estimateData.projectTimelineMonths
        ? feeAmount / estimateData.projectTimelineMonths
        : null;

    return {
      ...estimateData,
      categories,
      baseTotalCost,
      feePercentage,
      feeAmount,
      grandTotal,
      feePerMonth,
      totalCost,
      costPerSf,
    };
  };

  // Handle resolving unmapped items
  const handleResolveUnmapped = (item, action, payload) => {
    // Always persist the masterformatCode assignment to the DB so that the
    // change survives even when the upload wizard only saves metadata fields.
    // The persistMapping flag controls whether the tenant mapping store is also
    // updated (i.e. the "Remember for future estimates" checkbox).
    if (
      action === 'reassign'
      && payload.code?.code
      && initialEstimate.estimateId
      && item.lineItemId
    ) {
      estimatesApi
        .updateLineItem(initialEstimate.estimateId, item.lineItemId, {
          masterformatCode: payload.code.code,
          masterformatTitle: payload.code.title,
          persistMapping: !!payload.persistMapping,
          sourceDescription: item.description,
        })
        .catch((err) => {
          console.error('Failed to save reassignment for unmapped item:', err);
          showError('Reassignment applied locally, but saving to the estimate failed.');
        });
    }

    setEstimate(prev => {
      const updatedCategories = prev.categories.map(cat => {
        if (cat.categoryId !== item.categoryId) return cat;

        let updatedItems = [...(cat.lineItems || [])];

        switch (action) {
          case 'reassign':
            // Update the item's masterformatCode
            updatedItems = updatedItems.map(li =>
              li.lineItemId === item.lineItemId
                ? { ...li, masterformatCode: payload.code.code, isUnmapped: false }
                : li
            );
            break;

          case 'merge': {
            // Add this item's cost to the target item, then remove this item
            const targetIdx = updatedItems.findIndex(li => li.lineItemId === payload.targetId);
            if (targetIdx >= 0) {
              updatedItems[targetIdx] = {
                ...updatedItems[targetIdx],
                totalCost: (updatedItems[targetIdx].totalCost || 0) + (item.totalCost || 0),
              };
            }
            updatedItems = updatedItems.filter(li => li.lineItemId !== item.lineItemId);
            break;
          }

          case 'exclude':
            // Remove the item entirely
            updatedItems = updatedItems.filter(li => li.lineItemId !== item.lineItemId);
            break;

          default:
            break;
        }

        return { ...cat, lineItems: updatedItems };
      });

      return { ...prev, categories: updatedCategories };
    });
    setHasChanges(true);
  };

  // Drain any pending row-level edits. Each flush() commits its editValues
  // up through onUpdate (which is async via setEstimate) AND returns the
  // payload synchronously, so we can apply the payloads to the snapshot
  // we're about to serialize without waiting for React to re-render.
  const drainPendingFlushes = () => {
    const flushed = [];
    if (!pendingFlushesRef.current) return flushed;
    // Copy to a new array first — flush() will delete entries from the map
    // (via the cleanup effect when state transitions to VIEW).
    const flushes = Array.from(pendingFlushesRef.current.values());
    for (const flush of flushes) {
      const payload = flush();
      if (payload) flushed.push(payload);
    }
    return flushed;
  };

  const buildFinalEstimate = (extraPayloads = []) => {
    const byId = new Map(extraPayloads.map(p => [p.lineItemId, p]));
    const merged = byId.size === 0
      ? estimate
      : {
          ...estimate,
          categories: (estimate.categories || []).map(cat => ({
            ...cat,
            lineItems: (cat.lineItems || []).map(li =>
              byId.has(li.lineItemId) ? { ...li, ...byId.get(li.lineItemId) } : li
            ),
          })),
        };
    return recalculateTotals({
      ...merged,
      projectName,
      clientName,
      location,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const flushed = drainPendingFlushes();
      const finalEstimate = buildFinalEstimate(flushed);
      await onSave(finalEstimate);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save:', err);
      showError('Failed to save estimate. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (!onNext) return;
    setIsSaving(true);
    try {
      const flushed = drainPendingFlushes();
      const hadPendingEdits = flushed.length > 0;
      if (hasChanges || hadPendingEdits) {
        const finalEstimate = buildFinalEstimate(flushed);
        await onSave(finalEstimate);
        setHasChanges(false);
        onNext(finalEstimate);
      } else {
        onNext(buildFinalEstimate());
      }
    } catch (err) {
      console.error('Failed to save:', err);
      showError('Failed to save estimate. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const FEE_CATEGORY_CODE_DISPLAY = '00';

  // Calculate base total (excluding fee category) for fee display
  const baseTotalCost = (estimate.categories || [])
    .filter((cat) => cat.categoryCode !== FEE_CATEGORY_CODE_DISPLAY)
    .reduce((sum, cat) => {
      const catTotal = (cat.lineItems || []).reduce(
        (itemSum, item) => itemSum + (item.totalCost || 0),
        0
      );
      return sum + catTotal;
    }, 0);

  const totalCost = (estimate.categories || []).reduce((sum, cat) => {
    const catTotal = (cat.lineItems || []).reduce(
      (itemSum, item) => itemSum + (item.totalCost || 0),
      0
    );
    return sum + catTotal;
  }, 0);

  const totalLineItems = (estimate.categories || []).reduce(
    (sum, cat) => sum + (cat.lineItems?.length || 0),
    0
  );

  const aiEstimatedItems = (estimate.categories || []).reduce(
    (sum, cat) =>
      sum + (cat.lineItems?.filter((item) => item.costSource === 'ai' || item.aiEstimatedCost).length || 0),
    0
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 break-words">{projectName}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
            Based on: {initialEstimate.projectName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {totalLineItems} line items
            {aiEstimatedItems > 0 && (
              <span className="text-blue-600 dark:text-blue-400"> ({aiEstimatedItems} AI estimated)</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:flex-nowrap">
          {/* MasterFormat View Toggle — hidden for subcontractor (custom-mode) tenants */}
          {!isCustomMode && (
            <MasterFormatViewToggle
              viewMode={masterFormatViewMode}
              onToggle={setMasterFormatViewMode}
            />
          )}
          <div className="text-right lg:mr-4 flex-1 lg:flex-initial">
            <p className="text-sm text-gray-500 dark:text-gray-400">Current Total</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(totalCost)}</p>
          </div>
          {/* AI Estimate All button removed - estimation now happens per line item during editing */}
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            {onNext ? 'Back' : 'Cancel'}
          </Button>
          {onNext ? (
            <Button onClick={handleNext} disabled={isSaving || hasUnmappedItems}>
              {isSaving ? 'Saving...' : hasUnmappedItems ? `Resolve ${unmappedItems.length} item${unmappedItems.length !== 1 ? 's' : ''} first` : 'Next'}
            </Button>
          ) : (
            // Row edits auto-commit on focus exit, so we can't rely on
            // `hasChanges` to know whether the user has typed something —
            // pending edits live in row-local state until Done flushes them.
            <Button onClick={handleSave} disabled={isSaving || hasUnmappedItems}>
              {isSaving ? 'Saving...' : hasUnmappedItems ? `Resolve ${unmappedItems.length} item${unmappedItems.length !== 1 ? 's' : ''} first` : 'Done'}
            </Button>
          )}
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-yellow-800 dark:text-yellow-300 text-sm font-medium">
            {onNext ? 'You have unsaved changes — they will be saved automatically when you click Next' : 'You have unsaved changes'}
          </span>
        </div>
      )}

      {/* AI Estimation Info */}
      {aiEstimatedItems > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-blue-800 dark:text-blue-300 text-sm">
            {aiEstimatedItems} of {totalLineItems} line items have AI estimates.
            Click the info icon on each item to view the explanation.
            When editing an AI estimate, you'll be asked to explain your changes.
          </span>
        </div>
      )}

      {/* Unmapped Items Banner */}
      {hasUnmappedItems && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-200">
                  {unmappedItems.length} item{unmappedItems.length !== 1 ? 's' : ''} need{unmappedItems.length === 1 ? 's' : ''} your attention
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  These line items could not be matched to a standard MasterFormat code. Please resolve each one before saving.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUnmappedPanel(!showUnmappedPanel)}
            >
              {showUnmappedPanel ? 'Hide' : 'Review Items'}
            </Button>
          </div>

          {/* Resolution Panel */}
          {showUnmappedPanel && (
            <div className="mt-4 space-y-3">
              {unmappedItems.map((item) => (
                <UnmappedItemResolution
                  key={item.lineItemId}
                  item={item}
                  onResolve={handleResolveUnmapped}
                  existingItems={(estimate.categories || [])
                    .find(c => c.categoryId === item.categoryId)
                    ?.lineItems?.filter(li => li.masterformatCode && li.lineItemId !== item.lineItemId) || []}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      <div className="space-y-4">
        {isLegacyView ? (
          // Legacy view: grouped display with editable numeric fields (description read-only)
          <>
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-blue-800 dark:text-blue-300 text-sm">
                Legacy (1995) view: You can edit quantities and costs. Switch to Modern (2004+) to add/remove categories or edit descriptions.
              </span>
            </div>
            {displayCategories.map((legacyCategory) => (
              <LegacyCategoryDisplay
                key={legacyCategory.categoryId}
                legacyCategory={legacyCategory}
                baseTotalCost={baseTotalCost}
                onUpdateLineItem={(updatedItem) => {
                  // Find which modern category this item belongs to and update it
                  const originalCategoryId = updatedItem.originalCategoryId;
                  if (!originalCategoryId) return;

                  setEstimate((prev) => ({
                    ...prev,
                    categories: prev.categories.map((cat) => {
                      if (cat.categoryId === originalCategoryId) {
                        return {
                          ...cat,
                          lineItems: cat.lineItems.map((li) =>
                            li.lineItemId === updatedItem.lineItemId ? { ...li, ...updatedItem } : li
                          ),
                        };
                      }
                      return cat;
                    }),
                  }));
                  setHasChanges(true);
                }}
              />
            ))}
          </>
        ) : (
          // Modern view: full editable categories
          <>
            {[...(estimate.categories || [])]
              .sort((a, b) => (a.categoryCode || '').localeCompare(b.categoryCode || ''))
              .map((category) => (
              <CategoryEditor
                key={category.categoryId}
                category={category}
                onUpdate={handleUpdateCategory}
                onDelete={() => handleDeleteCategory(category.categoryId)}
                estimateId={estimate.estimateId}
                projectContext={projectContext}
                baseTotalCost={baseTotalCost}
                existingCategoryCodes={(estimate.categories || [])
                  .filter(cat => cat.categoryCode && cat.categoryId !== category.categoryId)
                  .map(cat => cat.categoryCode)}
                hideAIEstimate={hideAIEstimate}
                isCustomMode={isCustomMode}
                pendingFlushesRef={pendingFlushesRef}
              />
            ))}

            {/* Add Category Button — hidden for subcontractor (custom-mode) tenants */}
            {!isCustomMode && (
              <Button variant="outline" onClick={handleAddCategory}>
                + Add Category
              </Button>
            )}
          </>
        )}
      </div>

      {/* Supporting Documents */}
      {!hideDocuments && estimate.estimateId && (
        <EstimateDocumentsPanel estimateId={estimate.estimateId} editable={true} />
      )}

      {/* Delete Category Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Category"
        message="Are you sure you want to delete this category? This will remove all line items within it."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDeleteCategory}
        onCancel={cancelDeleteCategory}
      />

      {/* Mobile-only sticky bottom action bar. The top header overflows on
          phones and the Done button is easy to miss, leaving line-item edits
          unsaved. This keeps Save/Cancel one tap away from anywhere. */}
      <div className="lg:hidden sticky bottom-0 inset-x-0 -mx-4 sm:-mx-6 mt-6 px-4 sm:px-6 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 flex items-center gap-2 z-30 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">Current Total</p>
          <p className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">{formatCurrency(totalCost)}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
          {onNext ? 'Back' : 'Cancel'}
        </Button>
        {onNext ? (
          <Button size="sm" onClick={handleNext} disabled={isSaving || hasUnmappedItems}>
            {isSaving ? 'Saving…' : 'Next'}
          </Button>
        ) : (
          <Button size="sm" onClick={handleSave} disabled={isSaving || hasUnmappedItems}>
            {isSaving ? 'Saving…' : 'Done'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default EstimateTreeEditor;
